import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import { TableColumnsType } from 'antd';

import { useLocationName } from '../../../../hooks/useLocationName';
import { useUserContext } from '../../../../context/PermissionsContext';

import TitleUpdater from '../../../../components/common/TitleUpdater';
import FinancialData from '../../../../components/common/FinancialData';
import PercentageFormatter from '../../../../components/common/PercentageFormatter';
import QueryErrorBoundary from '../../../../components/error-handling';
import { Button, Footer, TitleBar } from '../../../../components';
import CollapsibleText from '../../../../components/generic/TextTruncation/CollapsibleText';

import TableV3, {
  ModifyVariablesArgs,
} from '../../../../components/generic/Table/TableV3';

import {
  FETCH_PROCESS_AREAS_IT_APP_SUMMARY,
  SET_PROCESS_AREAS_DEPENDENCY_VALUES,
} from './queries';
import {
  GetProcessAreaItApplicationsSummaryResponse,
  ProcessAreaItApplicationsSummary,
} from './types';

import { tableSortingMethods } from '../../../../utils';
import { decimalNumberFormatter } from '../../../../utils/numberFormatter';
import { useCallback } from 'react';
import { ProcessAreaAndTaggingTitle } from '../../constants';

const LEVEL_IDENTIFIERS = {
  IT_APPLICATION: 'ItApplicationLocationSummary',
  PROCESS_AREA: 'ItApplicationsProcessAreas',
  PROCESS: 'ItApplicationsProcesses',
};

const formatProcessAreasSummaryData = (
  data: ProcessAreaItApplicationsSummary[],
) => {
  return data.map((item) => {
    const {
      processAreas: children,
      processAreasCount,
      processesCount,
      minRevenue,
      maxRevenue,
      minPercentage,
      maxPercentage,
      ...rest
    } = item;

    const revenue = `${decimalNumberFormatter(
      minRevenue,
    )} to ${decimalNumberFormatter(maxRevenue)}`;
    const percentage = `${Math.round(minPercentage)} to ${Math.round(
      maxPercentage,
    )}%`;
    const description = `Supports ${processAreasCount} process areas and ${processesCount} underlying processes`;

    return {
      ...rest,
      description,
      revenue,
      percentage,
      children: children.map((processArea) => {
        const { processes: children, ...rest } = processArea;
        const newKey = `${processArea.uuid}-${processArea.itApplicationUuid}`;

        return {
          ...rest,
          rowKey: newKey,
          children: children?.map((process) => {
            const newKey = `${process.uuid}-${process.itApplicationUuid}`;

            return {
              ...process,
              rowKey: newKey,
            };
          }),
        };
      }),
    };
  });
};

const LocationProcessAreasSummary = () => {
  const navigate = useNavigate();

  const { locationId, processAreaId } = useParams();

  const { locationName, locationNameError } = useLocationName(locationId);

  const { canEdit } = useUserContext();

  const {
    data: locationITAppData,
    loading: locationITAppLoading,
    error: locationITAppError,
  } = useQuery<GetProcessAreaItApplicationsSummaryResponse>(
    FETCH_PROCESS_AREAS_IT_APP_SUMMARY,
    {
      variables: {
        data: locationId,
        ...(processAreaId && { processAreaUUID: processAreaId }),
      },
      skip: !locationId,
    },
  );

  const [
    updateApplicationMapping,
    { loading: updateApplicationMappingLoading },
  ] = useMutation(SET_PROCESS_AREAS_DEPENDENCY_VALUES, {
    refetchQueries: ['GetProcessAreaItApplicationsSummary'],
  });

  const ITAppsSummary =
    locationITAppData?.getProcessAreaItApplicationsSummary ?? [];

  const formattedData = formatProcessAreasSummaryData(ITAppsSummary);

  const loadingStates = locationITAppLoading || updateApplicationMappingLoading;

  const columns: TableColumnsType<ProcessAreaItApplicationsSummary> = [
    {
      title: 'Applications',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (applicationName: string) => (
        <div className="flex flex-col">
          <span className="custom-table--column ml-5">{applicationName}</span>
        </div>
      ),
    },
    {
      title: (
        <span>
          Revenue Supported <br />
          Annually
        </span>
      ),
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'right',
      width: 100,
      sorter: (a, b) => tableSortingMethods.number(a.minRevenue, b.minRevenue),
      render: (revenue: number, record) => {
        const isITApplevel =
          record.__typename === LEVEL_IDENTIFIERS.IT_APPLICATION;
        if (!isITApplevel) {
          return (
            <span className="custom-table--column">
              <FinancialData text={revenue} />
            </span>
          );
        }
      },
    },
    {
      title: (
        <span>
          % of Location <br /> Supported
        </span>
      ),
      dataIndex: 'percentage',
      key: 'percentage',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(a.minPercentage, b.minPercentage),
      render: (percentage: string | number, record) => {
        const isITApplevel =
          record.__typename === LEVEL_IDENTIFIERS.IT_APPLICATION;
        if (!isITApplevel) {
          return (
            <span className="custom-table--column">
              <PercentageFormatter value={percentage} />
            </span>
          );
        }
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      align: 'left',
      render: (description: string) => {
        return (
          <div className="text-sm font-normal text-black">
            <CollapsibleText text={description} />
          </div>
        );
      },
    },
  ];

  const modifyVariables = useCallback(
    ({ values, currentRecord }: ModifyVariablesArgs) => {
      const {
        uuid,
        itApplicationUuid: itApplicationsUuid,
        __typename,
      } = currentRecord;

      const dependencyType =
        __typename === LEVEL_IDENTIFIERS.PROCESS_AREA
          ? 'PROCESS_AREA'
          : 'PROCESS';

      const { description } = values;

      return {
        dependencyUuid: uuid,
        dependencyType: dependencyType,
        itApplicationValues: [
          {
            itApplicationsUuid,
            description,
          },
        ],
        shouldOmitUuid: true,
      };
    },
    [],
  );

  const formFields = [
    {
      label: 'Application Description',
      key: 'description',
      type: 'textArea',
    },
  ];

  // This is for avoiding showing edit button for both It App and Process levels (according to Figma)
  const excludeRows = [
    {
      identifier: '__typename',
      value: 'ItApplicationLocationSummary',
    },
  ];

  const handleProcessAreaView = (
    <Button
      type="primary"
      className="w-[222px] text-sm !p-1.5 mt-1 h-fit self-center"
      onClick={() =>
        navigate(
          `/cyber/location-level/${locationId}/process-areas-and-processes`,
        )
      }
    >
      Process Area View
    </Button>
  );

  return (
    <>
      <TitleUpdater title={`${locationName} Application Tagging Summary`} />
      <QueryErrorBoundary error={locationNameError}>
        <TitleBar
          title={ProcessAreaAndTaggingTitle}
          subtitle={`${locationName} Application Tagging Summary`}
          rightContent={processAreaId ? undefined : handleProcessAreaView}
        />
        <QueryErrorBoundary error={locationITAppError}>
          <TableV3
            data={formattedData}
            columns={columns}
            updateGQL={updateApplicationMapping}
            loading={loadingStates}
            editable
            editableOptions={{
              update: canEdit,
            }}
            formFields={formFields}
            inlineEditing
            excludeRows={excludeRows}
            modifyVariables={modifyVariables}
            rowKey="rowKey"
            nonSearchableColumns={['revenue', 'percentage', 'description']}
            searchPlaceholder="Search for Applications"
            searchOnlyPrincipalData={true}
          />
        </QueryErrorBoundary>
        <Footer goBack />
      </QueryErrorBoundary>
    </>
  );
};

export default LocationProcessAreasSummary;
